import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAnalyticsStore } from '~contexts/mobx';
import {
    analyticsToday,
    campaignRange,
    CampaignWindow,
} from '~containers/analytics/kpis/periods';
import type { KpiCampaignContext } from '~containers/analytics/kpis/types';
import type { ServedCampaign } from '~stores/analytics';
import { NHL_COINS_EDITION_ID } from '~containers/analytics/kpis/sources/nhl-coins-edition';

const NO_EDITION = 'No NHL Coins edition is served in this environment.';

const EDITIONS_FAILED =
    'The NHL Coins editions could not be loaded, so their figures cannot be framed.';

type EditionsState =
    | { status: 'loading' }
    | { status: 'ready'; editions: ServedCampaign[] }
    | { status: 'failed' };

export interface NhlCoinsEdition {
    editions: ServedCampaign[];
    edition: ServedCampaign | undefined;
    select: (id: string) => void;
    /** For the period capsule, which takes the edition's own bounds. */
    campaign: CampaignWindow | undefined;
    /** For the cards, which take the resolved window or the reason there is
     * none. */
    context: KpiCampaignContext;
}

/**
 * The NHL Coins editions the API serves, the one the page reads, and its
 * window.
 */
export const useNhlCoinsEdition = (): NhlCoinsEdition => {
    const analyticsStore = useAnalyticsStore();
    const [state, setState] = useState<EditionsState>({ status: 'loading' });
    const [selectedId, setSelectedId] = useState<string>();

    useEffect(() => {
        let cancelled = false;
        analyticsStore
            .fetchServedCampaigns()
            .then((campaigns) => {
                if (cancelled) return;
                setState(
                    campaigns === null
                        ? { status: 'failed' }
                        : {
                              status: 'ready',
                              editions: campaigns.filter(
                                  (campaign) =>
                                      campaign.kind === 'edition' &&
                                      campaign.gameKind === 'nhl-coins'
                              ),
                          }
                );
            })
            .catch(() => {
                if (!cancelled) setState({ status: 'failed' });
            });
        return () => {
            cancelled = true;
        };
    }, [analyticsStore]);

    const editions = state.status === 'ready' ? state.editions : [];
    const edition =
        editions.find((candidate) => candidate.id === selectedId) ??
        editions.find((candidate) => candidate.id === NHL_COINS_EDITION_ID) ??
        editions[0];

    const select = useCallback((id: string) => setSelectedId(id), []);

    const { campaign, context } = useMemo<
        Pick<NhlCoinsEdition, 'campaign' | 'context'>
    >(() => {
        if (state.status === 'loading') {
            return { campaign: undefined, context: { status: 'loading' } };
        }
        if (!edition) {
            return {
                campaign: undefined,
                context: {
                    status: 'unavailable',
                    reason:
                        state.status === 'failed'
                            ? EDITIONS_FAILED
                            : NO_EDITION,
                },
            };
        }
        const span: CampaignWindow = {
            startDate: edition.startDate,
            ...(edition.endDate && { endDate: edition.endDate }),
        };
        const today = analyticsToday();
        return {
            campaign: span,
            context: {
                status: 'ready',
                runsToday: !span.endDate || span.endDate >= today,
                toDate: {
                    ...campaignRange(span, today),
                    preset: { period: 'campaign', campaignId: edition.id },
                },
            },
        };
    }, [state.status, edition]);

    return { editions, edition, select, campaign, context };
};
